"use client";

// Section "Nos avantages" — bento grid 6 colonnes type editorial dashboard.
// Adapté au thème clair de Kessem : cartes blanches sur fond muted, accents
// cyan/violet, illustrations SVG originales. Animations subtiles déclenchées
// au scroll (drawing strokes, scan, fades) sans surcharger.

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Code2, Headphones } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SectionEyebrow, SectionTitle } from "../section-eyebrow";
import { ScrollReveal } from "../scroll-reveal";

// ───────────────────── card 1 — concentric circle w/ icon + accent bar

function SecureCard({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Card className="relative col-span-full overflow-hidden bg-card sm:col-span-3 lg:col-span-3">
      <CardContent className="pt-6">
        <div className="relative mx-auto flex aspect-square size-32 rounded-full border border-border before:absolute before:-inset-2 before:rounded-full before:border before:border-border/60">
          <svg
            viewBox="0 0 200 140"
            className="m-auto h-fit w-24"
            fill="none"
            aria-hidden
          >
            {/* layered concentric arcs evoking a 3D dome */}
            <ellipse cx="100" cy="80" rx="62" ry="22" className="stroke-muted-foreground/40" strokeWidth="1.4" />
            <ellipse cx="100" cy="68" rx="56" ry="18" className="stroke-muted-foreground/35" strokeWidth="1.3" />
            <ellipse cx="100" cy="58" rx="48" ry="15" className="stroke-muted-foreground/28" strokeWidth="1.2" />
            <ellipse cx="100" cy="50" rx="40" ry="12" className="stroke-muted-foreground/20" strokeWidth="1.1" />
            <ellipse cx="100" cy="44" rx="32" ry="9" className="stroke-muted-foreground/15" strokeWidth="1" />
            {/* shield icon in middle */}
            <g transform="translate(86 74)">
              <path
                d="M14 0 L28 6 V18 C28 26, 22 32, 14 36 C6 32, 0 26, 0 18 V6 Z"
                fill="url(#secureGrad)"
              />
              <path d="M9 18 L13 22 L20 14" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            <defs>
              <linearGradient id="secureGrad" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--aurora-cyan)" />
                <stop offset="100%" stopColor="var(--aurora-violet)" />
              </linearGradient>
            </defs>
            {/* horizontal accent line crossing through (the "primary bar") */}
            <line
              x1="3"
              y1="72"
              x2="197"
              y2="72"
              stroke="url(#secureGrad)"
              strokeWidth="6"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div className="relative z-10 mt-6 space-y-2 text-center">
          <h2 className="font-headline text-lg font-medium text-foreground">{title}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ───────────────────── card 3 — performance line chart in browser frame

function SpeedCard({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  // Simple original waveform (perf timeline) — generated points.
  const points = [
    [10, 90], [40, 70], [70, 80], [110, 55], [150, 65],
    [190, 40], [230, 50], [270, 28], [310, 35], [350, 18],
    [380, 22],
  ];
  const pathD = points
    .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
    .join(" ");
  const areaD = `${pathD} L 380 110 L 10 110 Z`;

  return (
    <Card className="relative col-span-full overflow-hidden bg-card sm:col-span-3 lg:col-span-3">
      <CardContent className="pt-6">
        <div className="pt-2 lg:px-2">
          <svg viewBox="0 0 386 123" className="w-full text-muted-foreground" fill="none" aria-hidden>
            <rect width="386" height="123" rx="10" className="fill-muted/30" />
            {/* browser chrome */}
            <circle cx="22" cy="20" r="3" className="fill-rose-400/70" />
            <circle cx="32" cy="20" r="3" className="fill-amber-300/70" />
            <circle cx="42" cy="20" r="3" className="fill-emerald-400/70" />
            <rect x="60" y="14" width="120" height="12" rx="3" className="fill-muted-foreground/15" />
            <rect x="190" y="14" width="60" height="12" rx="3" className="fill-muted-foreground/10" />
            {/* axis */}
            <line x1="10" y1="110" x2="380" y2="110" stroke="currentColor" strokeOpacity="0.18" strokeWidth="1" />
            {/* gradient area */}
            <motion.path
              d={areaD}
              fill="url(#speedArea)"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.7 }}
            />
            <motion.path
              d={pathD}
              stroke="url(#speedLine)"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="900"
              initial={{ strokeDashoffset: 900 }}
              whileInView={{ strokeDashoffset: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            />
            {/* end-point dot */}
            <motion.circle
              cx="380"
              cy="22"
              r="4"
              className="fill-aurora-cyan"
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.4, delay: 1.4, ease: [0.34, 1.56, 0.64, 1] }}
            />
            <defs>
              <linearGradient id="speedLine" x1="0" x2="1">
                <stop offset="0%" stopColor="var(--aurora-violet)" />
                <stop offset="100%" stopColor="var(--aurora-cyan)" />
              </linearGradient>
              <linearGradient id="speedArea" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--aurora-cyan)" stopOpacity="0.22" />
                <stop offset="100%" stopColor="var(--aurora-cyan)" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className="relative z-10 mt-10 space-y-2 text-center">
          <h2 className="font-headline text-lg font-medium text-foreground">{title}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ───────────────────── card 4 — icon + heading + waveform bars

function WaveformCard({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  // Original bar pattern — a smooth sine + jitter.
  const N = 38;
  const bars = Array.from({ length: N }, (_, i) => {
    const t = i / (N - 1);
    const base = Math.sin(t * Math.PI * 2.4) * 0.5 + 0.5;
    const jitter = ((i * 9301 + 49297) % 233280) / 233280;
    return 0.2 + base * 0.6 + jitter * 0.18;
  });

  return (
    <Card className="relative col-span-full overflow-hidden bg-card lg:col-span-3">
      <CardContent className="grid pt-6 sm:grid-cols-2">
        <div className="relative z-10 flex flex-col justify-between space-y-12 lg:space-y-6">
          <div className="relative flex aspect-square size-12 rounded-full border border-border before:absolute before:-inset-2 before:rounded-full before:border before:border-border/60">
            <Code2 className="m-auto size-5 text-foreground/80" strokeWidth={1.4} />
          </div>
          <div className="space-y-2">
            <h2 className="font-headline text-lg font-medium text-foreground">{title}</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="relative -mb-6 -mr-6 mt-6 h-fit rounded-tl-[var(--radius)] border-l border-t border-border p-6 sm:ml-6">
          <div className="absolute left-3 top-2 flex gap-1">
            <span className="block size-2 rounded-full border border-border bg-muted/40" />
            <span className="block size-2 rounded-full border border-border bg-muted/40" />
            <span className="block size-2 rounded-full border border-border bg-muted/40" />
          </div>
          <svg viewBox="0 0 320 120" className="w-full" fill="none" aria-hidden>
            <line x1="0" y1="80" x2="320" y2="80" stroke="currentColor" className="text-border" strokeWidth="1" />
            {bars.map((h, i) => {
              const x = (i / (N - 1)) * 308 + 6;
              const barH = h * 90;
              return (
                <motion.rect
                  key={i}
                  x={x - 2.4}
                  width="4.8"
                  rx="1.5"
                  y={80 - barH}
                  height={barH}
                  fill="url(#waveGrad)"
                  initial={{ scaleY: 0, transformOrigin: `${x}px 80px` }}
                  whileInView={{ scaleY: 1 }}
                  viewport={{ once: false, amount: 0.3 }}
                  transition={{
                    duration: 0.5,
                    delay: i * 0.018,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  style={{ transformOrigin: `${x}px 80px` }}
                />
              );
            })}
            <defs>
              <linearGradient id="waveGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--aurora-cyan)" />
                <stop offset="100%" stopColor="var(--aurora-violet)" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </CardContent>
    </Card>
  );
}

// ───────────────────── card 5 — relationship: name pills with avatars

const PEOPLE = [
  { name: "Garry", initials: "GK", side: "right" as const, hue: "cyan" },
  { name: "Vous", initials: "VS", side: "left" as const, hue: "violet" },
  { name: "Direct", initials: "//", side: "right" as const, hue: "cyan" },
];

function DirectCard({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Card className="relative col-span-full overflow-hidden bg-card lg:col-span-3">
      <CardContent className="grid h-full pt-6 lg:grid-cols-2">
        <div className="relative z-10 flex flex-col justify-between space-y-12 lg:space-y-6">
          <div className="relative flex aspect-square size-12 rounded-full border border-border before:absolute before:-inset-2 before:rounded-full before:border before:border-border/60">
            <Headphones className="m-auto size-5 text-foreground/80" strokeWidth={1.4} />
          </div>
          <div className="space-y-2">
            <h2 className="font-headline text-lg font-medium text-foreground">{title}</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="relative mt-6 before:absolute before:inset-0 before:mx-auto before:w-px before:bg-border sm:-my-6 sm:pr-2">
          <div className="relative flex h-full flex-col justify-center space-y-6 py-6 pr-2">
            {PEOPLE.map((p, i) => {
              const isRight = p.side === "right";
              return (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, x: isRight ? 16 : -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false, amount: 0.4 }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className={`relative flex items-center gap-2 ${
                    isRight
                      ? "w-1/2 justify-end ml-auto"
                      : "w-1/2 mr-auto"
                  }`}
                >
                  {isRight ? (
                    <>
                      <span className="block rounded border border-border bg-background px-2 py-1 text-xs shadow-sm">
                        {p.name}
                      </span>
                      <Avatar initials={p.initials} hue={p.hue} />
                    </>
                  ) : (
                    <>
                      <Avatar initials={p.initials} hue={p.hue} />
                      <span className="block rounded border border-border bg-background px-2 py-1 text-xs shadow-sm">
                        {p.name}
                      </span>
                    </>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Avatar({ initials, hue }: { initials: string; hue: string }) {
  const grad =
    hue === "violet"
      ? "linear-gradient(135deg, var(--aurora-violet), var(--aurora-magenta))"
      : "linear-gradient(135deg, var(--aurora-cyan), var(--aurora-blue))";
  return (
    <div
      className="size-7 rounded-full bg-background ring-4 ring-background"
      style={{ backgroundImage: grad }}
      role="presentation"
    >
      <span className="flex size-full items-center justify-center font-mono text-[10px] font-semibold text-white">
        {initials}
      </span>
    </div>
  );
}

// ───────────────────── section root

export function Advantages() {
  const t = useTranslations("advantages");
  const items = t.raw("items") as Array<{ title: string; description: string }>;

  return (
    <section className="relative py-12 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-5xl lg:px-8">
        <ScrollReveal as="header" className="mb-10 flex flex-col gap-5 sm:max-w-2xl">
          <div className="flex items-center justify-between">
            <SectionEyebrow>{t("eyebrow")}</SectionEyebrow>
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 sm:inline">
              {t("annotation")}
            </span>
          </div>
          <SectionTitle text={t("title")} italicLastWords={2} />
          <p data-blend className="text-base sm:text-lg">
            {t("subtitle")}
          </p>
        </ScrollReveal>

        <div className="relative">
          <div className="relative z-10 grid grid-cols-6 gap-3">
            <SpeedCard
              title={items[0]?.title ?? "Performance"}
              description={items[0]?.description ?? ""}
            />
            <SecureCard
              title={items[1]?.title ?? "Sécurité"}
              description={items[1]?.description ?? ""}
            />
            <DirectCard
              title={items[2]?.title ?? "Direct"}
              description={items[2]?.description ?? ""}
            />
            <WaveformCard
              title={items[3]?.title ?? "Tech moderne"}
              description={items[3]?.description ?? ""}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

